import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, CardContent, CardFooter, CardHeader, CardTitle } from '@pagespeedify/ui';
import MarkdownRenderer from './MarkdownRenderer';
import type { ClarificationActionMode, ClarificationSelectionDraft } from '../../types/json-render';

interface MonitoringPreplanOption {
    label: string;
    value: string;
    variant?: 'default' | 'outline' | 'secondary' | 'destructive';
    actionId?: string;
    expiresAt?: string;
}

interface MonitoringPreplanBaselineOption {
    id: string;
    websiteUrl: string;
    formFactor: 'mobile' | 'desktop';
    metricsToTrack?: string[];
}

interface MonitoringPreplanCurrentBaseline {
    monitoringId?: string | null;
    websiteUrl?: string | null;
    formFactor?: 'mobile' | 'desktop';
    metricsToTrack?: string[];
}

export interface MonitoringPreplanClarificationProps {
    question: string;
    monitoringOptions?: MonitoringPreplanBaselineOption[];
    currentMonitoring?: MonitoringPreplanCurrentBaseline | null;
    actions?: MonitoringPreplanOption[];
    onSubmit?: (option: MonitoringPreplanOption & { monitoringId?: string; monitoringUrl?: string }) => void;
    isLoading?: boolean;
    loadingActionId?: string | null;
    activeActionIds?: string[];
    forceDisabled?: boolean;
    disabledReason?: string;
    allowManualUrl?: boolean;
    suppressionReason?: string | null;
    actionMode?: ClarificationActionMode;
    onSelectionChange?: (draft: ClarificationSelectionDraft) => void;
}

function isValidMonitoringUrl(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return false;
    try {
        const parsed = new URL(trimmed);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

export default function MonitoringPreplanClarification({
    question,
    monitoringOptions = [],
    currentMonitoring = null,
    actions = [],
    onSubmit,
    isLoading = false,
    loadingActionId = null,
    activeActionIds = [],
    forceDisabled = false,
    disabledReason,
    allowManualUrl = true,
    suppressionReason = null,
    actionMode = 'full',
    onSelectionChange
}: MonitoringPreplanClarificationProps) {
    const [selectedMonitoringId, setSelectedMonitoringId] = useState<string>(currentMonitoring?.monitoringId || '');
    const [monitoringUrl, setMonitoringUrl] = useState<string>('');

    useEffect(() => {
        setSelectedMonitoringId(currentMonitoring?.monitoringId || '');
    }, [currentMonitoring?.monitoringId]);

    const activeSet = useMemo(() => new Set(activeActionIds), [activeActionIds]);
    const now = Date.now();
    const hasValidManualUrl = isValidMonitoringUrl(monitoringUrl);
    const anyActionInFlight = isLoading || !!loadingActionId;
    const manualUrlAllowed = allowManualUrl !== false;

    useEffect(() => {
        const draft: ClarificationSelectionDraft = {};
        if (selectedMonitoringId.trim() !== '') {
            draft.monitoringId = selectedMonitoringId.trim();
        }
        if (hasValidManualUrl) {
            draft.monitoringUrl = monitoringUrl.trim();
        }
        onSelectionChange?.(draft);
    }, [hasValidManualUrl, monitoringUrl, onSelectionChange, selectedMonitoringId]);

    return (
        <div className="overflow-hidden rounded-md border border-border bg-card">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">
                    Monitoring Baseline
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-3">
                <MarkdownRenderer
                    content={question}
                    className="prose-p:text-sm prose-p:mb-0 prose-p:text-foreground prose-strong:text-foreground"
                />

                {currentMonitoring?.websiteUrl && (
                    <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground">
                        <div className="font-medium">Current baseline</div>
                        <div className="mt-1">{currentMonitoring.websiteUrl}</div>
                        <div className="text-muted-foreground">
                            {currentMonitoring.formFactor || 'mobile'}
                            {Array.isArray(currentMonitoring.metricsToTrack) && currentMonitoring.metricsToTrack.length > 0
                                ? ` | ${currentMonitoring.metricsToTrack.join(', ')}`
                                : ''}
                        </div>
                    </div>
                )}

                {monitoringOptions.length > 0 && (
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-foreground">Reuse existing baseline</label>
                        <select
                            value={selectedMonitoringId}
                            onChange={(event) => setSelectedMonitoringId(event.target.value)}
                            disabled={forceDisabled || anyActionInFlight || actionMode === 'read_only'}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
                        >
                            <option value="">None</option>
                            {monitoringOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.websiteUrl} ({option.formFactor})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {manualUrlAllowed ? (
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-foreground">Or attach monitoring URL</label>
                        <input
                            type="url"
                            placeholder="https://example.com"
                            value={monitoringUrl}
                            onChange={(event) => setMonitoringUrl(event.target.value)}
                            disabled={forceDisabled || anyActionInFlight || actionMode === 'read_only'}
                            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
                        />
                        {monitoringUrl.trim() !== '' && !hasValidManualUrl && (
                            <p className="text-[11px] text-red-500">Enter a valid http(s) URL.</p>
                        )}
                    </div>
                ) : (
                    <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                        {suppressionReason === 'mobile_native_no_web'
                            ? 'Manual URL attachment is disabled for mobile-native repositories without web context.'
                            : 'Manual URL attachment is currently disabled for this repository context.'}
                    </div>
                )}
            </CardContent>
            {onSubmit && actions.length > 0 && actionMode === 'full' && (
                <CardFooter className="pt-0 pb-3">
                    <div className="flex flex-wrap gap-2">
                        {actions.map((option, index) => {
                            const expiresAtMs = option.expiresAt ? new Date(option.expiresAt).getTime() : Number.NaN;
                            const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs <= now;
                            const isInactive = !!option.actionId && activeSet.size > 0 && !activeSet.has(option.actionId);
                            const requiresMonitoringSelection = option.value === 'attach_monitoring_preplan';
                            const hasSelection = selectedMonitoringId.trim() !== '' || (manualUrlAllowed && hasValidManualUrl);
                            const disabled = forceDisabled
                                || anyActionInFlight
                                || (requiresMonitoringSelection && !hasSelection);
                            const isClickedAction = !!loadingActionId && !!option.actionId && loadingActionId === option.actionId;
                            const actionTitle = forceDisabled
                                ? (disabledReason || 'Action is unavailable.')
                                : isInactive
                                    ? 'Updating options...'
                                    : isExpired
                                        ? 'Updating options...'
                                        : (requiresMonitoringSelection && !hasSelection
                                            ? 'Select a baseline or enter a valid URL.'
                                            : undefined);

                            return (
                                <Button
                                    key={`${option.actionId || option.value}-${index}`}
                                    variant={option.variant || 'outline'}
                                    size="sm"
                                    disabled={disabled}
                                    title={actionTitle}
                                    onClick={() => onSubmit({
                                        ...option,
                                        ...(selectedMonitoringId.trim() !== '' ? { monitoringId: selectedMonitoringId.trim() } : {}),
                                        ...(hasValidManualUrl ? { monitoringUrl: monitoringUrl.trim() } : {})
                                    })}
                                    className="h-7 px-3 text-xs"
                                >
                                    {isClickedAction ? (
                                        <>
                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        option.label
                                    )}
                                </Button>
                            );
                        })}
                    </div>
                </CardFooter>
            )}
        </div>
    );
}
